import { defineStore } from "pinia";

import applicationsApiService from "@/services/modules/applications";

import type { EXTRA_DEAL_VIEW_REQUESTS } from "@/helpers/constants/deals";
import type { IDataOrchestrationLog } from "@/models/applications";

interface UseApplicationsState {
  allDataOrchestrationLogs: IDataOrchestrationLog[];
  dataOrchestrationLog: IDataOrchestrationLog | null;
  gettingOrchestrationLogs: boolean;
  canSeeDealActivity: boolean;
  shouldFetch: Record<(typeof EXTRA_DEAL_VIEW_REQUESTS)[number], boolean>;
  showVersionHistoryOverlay: boolean;
}

const getDefaultState = (): UseApplicationsState => ({
  allDataOrchestrationLogs: [],
  dataOrchestrationLog: null,
  gettingOrchestrationLogs: false,
  canSeeDealActivity: false,
  showVersionHistoryOverlay: false,
  shouldFetch: {
    notes: true,
    businessNotes: true,
    offers: true,
  },
});

const useApplicationsStore = defineStore("applications", {
  state: () => getDefaultState(),
  getters: {
    activeDataOrchestrationLog: ({ dataOrchestrationLog }) =>
      dataOrchestrationLog,
    shouldFetchNotes: ({ shouldFetch }) => shouldFetch.notes,
    shouldFetchBusinessNotes: ({ shouldFetch }) => shouldFetch.businessNotes,
    shouldFetchOffers: ({ shouldFetch }) => shouldFetch.offers,
  },
  actions: {
    async getLatestDataOrchestrationLog({ appId }: { appId: string }) {
      this.dataOrchestrationLog = null;
      await this.getAllDataOrchestrationLogs({ appId });
      const activeLogId = this.allDataOrchestrationLogs[0]?.id;

      if (!activeLogId) {
        return;
      }

      await this.getDataOrchestrationLog({ appId, logId: activeLogId });
    },
    async getAllDataOrchestrationLogs({ appId }: { appId: string }) {
      try {
        this.gettingOrchestrationLogs = true;
        this.allDataOrchestrationLogs =
          await applicationsApiService.getDataOrchestrationLogs(appId);
      } finally {
        this.gettingOrchestrationLogs = false;
      }
    },
    async getDataOrchestrationLog({
      appId,
      logId,
    }: {
      appId: string;
      logId: string;
    }) {
      this.dataOrchestrationLog =
        await applicationsApiService.getDataOrchestrationLog(appId, logId);
    },
    updateDataOrchestrationLog(log: IDataOrchestrationLog) {
      this.dataOrchestrationLog = log;
    },
    async updateDataOrchestrationMasterStatus(payload: {
      appId: string;
      logId: string;
      master_status: number;
    }) {
      await applicationsApiService.updateDataOrchestrationMasterStatus(
        payload.appId,
        payload.logId,
        payload.master_status
      );
    },
    updateFetchServiceStatus(
      services: (typeof EXTRA_DEAL_VIEW_REQUESTS)[number][],
      status: boolean
    ) {
      services.forEach((service) => (this.shouldFetch[service] = status));
    },
  },
});

export default useApplicationsStore;
